import React, { useState, useEffect } from "react";
import { useAppStore } from "./AppStore";
import { PhoneCall, ShieldAlert, Pencil, Check, X, UserRound, Hash } from "lucide-react";

export default function SosDialer({ open, onClose, startEditing = false }) {
  const { emergency, setEmergency } = useAppStore();
  const [editing, setEditing] = useState(startEditing);
  const [name, setName] = useState(emergency.name);
  const [phone, setPhone] = useState(emergency.phone);

  useEffect(() => {
    if (open) {
      setEditing(startEditing);
      setName(emergency.name);
      setPhone(emergency.phone);
    }
  }, [open, startEditing, emergency]);

  if (!open) return null;

  const telHref = `tel:${(phone || "").replace(/[^0-9+]/g, "")}`;
  const save = () => {
    if (!phone.trim()) return;
    setEmergency({ name: name.trim() || "Emergency Contact", phone: phone.trim() });
    setEditing(false);
  };

  return (
    <div className="absolute inset-0 z-[60] flex items-end bg-black/50" onClick={onClose}>
      <div className="gd-enter w-full rounded-t-[28px] bg-white p-6 pb-8" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-[#e6e9f5]" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="grid h-11 w-11 place-items-center rounded-full bg-[#fdece9]"><ShieldAlert size={22} className="text-[#e0533d]" /></div>
            <div>
              <h3 className="text-lg font-black text-[#1f2a52]">Emergency SOS</h3>
              <p className="text-xs font-semibold text-[#9aa3c0]">One tap to call for help</p>
            </div>
          </div>
          <button onClick={onClose} className="text-[#a3abc9]"><X size={20} /></button>
        </div>

        {!editing ? (
          <>
            {/* current programmed contact */}
            <div className="mt-4 flex items-center gap-3 rounded-2xl bg-[#f7f8fd] p-4">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-[#e0533d] font-extrabold text-white">
                {(emergency.name || "?").trim().charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#9aa3c0]">Emergency Number</p>
                <p className="text-sm font-extrabold text-[#1f2a52]">{emergency.name}</p>
                <p className="text-sm font-bold text-[#e0533d]">{emergency.phone}</p>
              </div>
              <button onClick={() => setEditing(true)} className="gd-tap grid h-9 w-9 place-items-center rounded-full bg-white text-[#7b86ab] shadow"><Pencil size={16} /></button>
            </div>

            {/* call button */}
            <a href={telHref} className="gd-tap mt-4 flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-lg font-extrabold text-white"
              style={{ background: "linear-gradient(180deg,#ff5b4a,#c0231a)", boxShadow: "0 14px 28px rgba(192,35,26,.4)" }}>
              <PhoneCall size={22} /> Call Now
            </a>
            <p className="mt-3 text-center text-[11px] font-semibold text-[#9aa3c0]">
              Calls {emergency.name} instantly and shares your live location.
            </p>
            <button onClick={() => setEditing(true)} className="mt-2 w-full rounded-2xl py-2.5 text-sm font-bold text-[#3b57c9]">
              Program a different number
            </button>
          </>
        ) : (
          <>
            {/* edit / program form */}
            <div className="mt-4 space-y-4">
              <div>
                <label className="gd-label">CONTACT NAME</label>
                <div className="flex items-center gap-2.5 rounded-2xl border border-[#e6e9f5] bg-[#f7f8fd] px-4 py-3.5">
                  <UserRound size={17} className="text-[#a3abc9]" />
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Mom, Dad, Guardian…" className="w-full bg-transparent text-sm font-medium text-[#1f2a52] outline-none placeholder:text-[#b6bdd6]" />
                </div>
              </div>
              <div>
                <label className="gd-label">EMERGENCY PHONE NUMBER</label>
                <div className="flex items-center gap-2.5 rounded-2xl border border-[#e6e9f5] bg-[#f7f8fd] px-4 py-3.5">
                  <Hash size={17} className="text-[#a3abc9]" />
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 (555) 000-0000" inputMode="tel" className="w-full bg-transparent text-sm font-medium text-[#1f2a52] outline-none placeholder:text-[#b6bdd6]" />
                </div>
              </div>
            </div>
            <button onClick={save} disabled={!phone.trim()} className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl py-4 font-extrabold text-white transition-all"
              style={{ background: phone.trim() ? "linear-gradient(180deg,#3f5ccb,#2f47a8)" : "#c7cde6" }}>
              <Check size={18} /> Save Emergency Number
            </button>
            <button onClick={() => setEditing(false)} className="mt-2 w-full rounded-2xl py-2.5 text-sm font-bold text-[#5b648a]">Cancel</button>
          </>
        )}
      </div>
    </div>
  );
}
